import { useCallback, useMemo, useState } from "react";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";

// Buffer rows rendered above/below the viewport. Higher = the blank flash
// on a fast fling opens later (rows are already mounted further out), at the
// cost of more mounted rows — still trivial vs the full dataset. The flash
// is inherent to virtualization (compositor scroll outruns main-thread row
// mounting), so this reduces it rather than eliminating it.
const OVERSCAN_ROWS = 25;

// Below this row count we don't virtualize at all — every row is mounted and
// scrolling is fully native, exactly like the (non-virtualized) horizontal
// axis, so there's zero blank flash. Virtualization only earns its keep (and
// its fling-flash tradeoff) once mounting every row would hurt initial load,
// which is a many-hundreds-of-rows problem. A few hundred cheap memoized rows
// mount instantly.
const MIN_ROWS_TO_VIRTUALIZE = 200;

/**
 * Row virtualization for TableElem, take two. The original implementation
 * (utils/useTableVirtualizer.js, removed in the perf cleanup) looked for its
 * vertical scroll container by walking up to the first ancestor with *any*
 * overflow auto/scroll — which matched the StickyHorizontalScrollbar wrapper
 * (overflow-x: auto; overflow-y: hidden). That element never scrolls
 * vertically and is as tall as the table itself, so the virtualizer
 * considered every row visible and mounted all of them — a no-op that still
 * paid for a ResizeObserver per row.
 *
 * This version:
 * - detects the scroll container by overflowY ALONE, and falls back to
 *   window-scrolling (useWindowVirtualizer) when no ancestor scrolls
 *   vertically — i.e. the page itself is the scroller
 * - treats row height as fixed (it is — TableRowElem hard-sets
 *   tableConfig.rowHeight || ROW_HEIGHT), so no measureElement and no
 *   per-row ResizeObservers
 * - the consumer positions rows absolutely (top = index * rowHeight) inside
 *   a relative, fixed-height container. NOT the usual translateY wrapper:
 *   an ancestor transform becomes the containing block for position:sticky
 *   descendants in Chromium, which would break the frozen first column
 *   (freezeFirstCol). Absolute rows have constant offsets, so scrolling
 *   causes zero relayout either way — rows just mount/unmount at the edges.
 * - virtualizes only above MIN_ROWS_TO_VIRTUALIZE, and can be disabled
 *   outright (enabled: false) for expandComp tables (expanded rows have
 *   variable height, which fixed-size virtualization can't represent). In
 *   both non-virtualized cases every row renders in plain flow, and the hook
 *   reports isVirtualizing:false so the consumer skips absolute positioning.
 */
export const useTableRowVirtualizer = ({ data, rowHeight, enabled = true }) => {
  // gate: eligible (not expandComp) AND big enough to be worth it
  const isVirtualizing = enabled && data.length > MIN_ROWS_TO_VIRTUALIZE;

  const [scrollConfig, setScrollConfig] = useState({
    scrollElement: null,
    scrollMargin: 0,
  });

  const rowsContainerRef = useCallback((node) => {
    if (!node) return;

    let el = node.parentElement;
    let scrollElement = null;
    while (el) {
      if (/(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
        scrollElement = el;
        break;
      }
      el = el.parentElement;
    }

    /**
     * scrollMargin = where the rows begin in the scroller's coordinate
     * space, so the virtualizer accounts for the header (and anything else
     * above the table) when mapping scroll offset -> visible row range.
     */
    const scrollMargin = scrollElement
      ? node.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop
      : node.getBoundingClientRect().top + window.scrollY;

    setScrollConfig({ scrollElement, scrollMargin });
  }, []);

  /**
   * Rules-of-hooks force both variants to be instantiated unconditionally;
   * only the one matching the detected scroll context is actually read.
   * The idle one is passed count 0 so it does no range math and observes
   * nothing meaningful.
   */
  const useWindowScrolling = !scrollConfig.scrollElement;

  const elementVirtualizer = useVirtualizer({
    count: isVirtualizing && !useWindowScrolling ? data.length : 0,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN_ROWS,
    getScrollElement: () => scrollConfig.scrollElement,
    scrollMargin: scrollConfig.scrollMargin,
  });

  const windowVirtualizer = useWindowVirtualizer({
    count: isVirtualizing && useWindowScrolling ? data.length : 0,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN_ROWS,
    scrollMargin: scrollConfig.scrollMargin,
  });

  const rowVirtualizer = useWindowScrolling
    ? windowVirtualizer
    : elementVirtualizer;

  /**
   * Not-virtualizing mode (small table, or expandComp): hand back the full
   * index range in the same shape getVirtualItems returns, natural height.
   */
  const allRows = useMemo(
    () =>
      isVirtualizing ? null : data.map((_, index) => ({ index })),
    [isVirtualizing, data]
  );

  if (!isVirtualizing) {
    return {
      rowsContainerRef,
      virtualRows: allRows,
      totalHeight: null, // null -> container keeps its natural flow height
      isVirtualizing: false,
    };
  }

  return {
    rowsContainerRef,
    virtualRows: rowVirtualizer.getVirtualItems(),
    totalHeight: rowVirtualizer.getTotalSize(),
    isVirtualizing: true,
  };
};
